import { Suspense, useEffect } from 'react';
import { Outlet, useParams, useLocation, Navigate } from 'react-router-dom';
import { LanguageProvider } from '../contexts/LanguageContext';
import { slugToLang, SUPPORTED_SLUGS } from '../hooks/useLocalePath';
import Header from '../components/Header';
import Footer from '../components/Footer';

function PageLoader() {
    return (
        <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg)' }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}>
                <div style={{ width: 32, height: 32, border: '2px solid var(--orange)', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                <p style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--muted)', letterSpacing: '.1em', textTransform: 'uppercase' }}>Loading...</p>
            </div>
        </div>
    );
}

export default function LangLayout() {
    const { lang } = useParams<{ lang: string }>();
    const { pathname, hash, key } = useLocation();

    // Scroll to top on every navigation (skip when hash is present — let page handle anchors)
    useEffect(() => {
        if (!hash) {
            // Reset both html and body scroll — CSS overflow setup makes body
            // the actual scroll container, so window.scrollTo alone isn't enough.
            window.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior });
            document.body.scrollTop = 0;
            document.documentElement.scrollTop = 0;
        }
    }, [pathname, hash, key]);

    // Redirect invalid lang slugs to /en
    if (!lang || !(SUPPORTED_SLUGS as readonly string[]).includes(lang)) {
        return <Navigate to="/en" replace />;
    }

    const language = slugToLang(lang);

    return (
        <LanguageProvider forcedLanguage={language}>
            <Header />
            <Suspense fallback={<PageLoader />}>
                <Outlet />
            </Suspense>
            <Footer />
        </LanguageProvider>
    );
}
